/**
 * Single source of truth for every published trust fact: subprocessors, retention,
 * security measures, and the DPA version pinned on /dpa.
 *
 * /security, /privacy and /dpa all read this module, so a vendor or retention change
 * is one edit rather than three pages that quietly drift apart.
 *
 * Facts below were verified against the primary source on 2026-09-09:
 *  - OpenRouter default storage: https://openrouter.ai/docs/guides/privacy/data-collection
 *    ("OpenRouter does not store your prompts or responses, unless you opt in"; both
 *    the private input/output logging and the 1% discount option are off by default)
 *    and https://openrouter.ai/privacy ("OpenRouter does not use your Inputs or
 *    Outputs for model training.")
 *  - Modal endpoint data policy as OpenRouter publishes it for the provider table on
 *    https://openrouter.ai/docs/guides/privacy/provider-logging (source data:
 *    https://openrouter.ai/api/frontend/v1/all-providers -> Modal: training false,
 *    retainsPrompts false, headquarters US). Modal policy:
 *    https://modal.com/legal/privacy-policy
 *  - Anthropic default API retention: 30 days, per
 *    https://privacy.claude.com/en/articles/7996866-how-long-do-you-store-personal-data
 *    ("automatically delete inputs and outputs on our backend within 30 days of receipt
 *    or generation"). Zero-data-retention exists as a separate agreement; we do not have one.
 *  - Routing config: functions/src/config.ts (AI_PROVIDER=openrouter, model z-ai/glm-5.3,
 *    OPENROUTER_PROVIDER_ORDER=Modal, Z.AI on the ignore list, OPENROUTER_WEB_SEARCH=false,
 *    AI_FALLBACK_PROVIDER=anthropic with claude-sonnet-4-6 and ANTHROPIC_WEB_SEARCH=true).
 *  - Retention rule: functions/src/retention.ts (365 days, measured from createdAt).
 *  - Deletion behaviour: functions/src/account.ts (deleteMyData).
 */
import { PAYMENTS_ENABLED } from '../lib/config'

/** Days a session is kept, measured from the day it was created (functions/src/retention.ts). */
export const RETENTION_DAYS = 365

/** Advance notice we give before a new subprocessor starts processing customer data. */
export const SUBPROCESSOR_NOTICE_DAYS = 30

export const DPA_VERSION = '1.0'
export const DPA_EFFECTIVE_DATE = '2026-09-09'
export const DPA_EFFECTIVE_DATE_LABEL = 'September 9, 2026'

/** Common Paper DPA Standard Terms the cover page incorporates by reference. */
export const COMMON_PAPER_DPA_VERSION = '1.1'
export const COMMON_PAPER_DPA_URL = 'https://commonpaper.com/standards/data-processing-agreement/1.1'

export const LAST_UPDATED = 'September 9, 2026'

/** Legal operator of Day One, as named in the Terms of Service. */
export const OPERATOR = {
  legalName: 'MVP Club Consulting',
  address: 'Denver, Colorado, United States',
  privacyEmail: 'privacy@mvpclub.ai',
  securityEmail: 'security@mvpclub.ai',
  generalEmail: 'hello@mvpclub.ai',
} as const

export interface Subprocessor {
  name: string
  /** Where this vendor sits in the stack: hosting, primary model, fallback model, and so on. */
  role: string
  purpose: string
  /** What the vendor receives. */
  data: string
  location: string
  /** What the vendor says it keeps, in its own published terms. */
  retention: string
  policyUrl: string
}

export const SUBPROCESSORS: Subprocessor[] = [
  {
    name: 'Google Cloud / Firebase',
    role: 'Hosting',
    purpose: 'Hosting, database, authentication',
    data: 'Session content, uploaded document text, and account identifiers (display name, email address)',
    location: 'United States. Data at rest in us-east1, compute in us-central1.',
    retention: `Holds the primary copy. Sessions are deleted ${RETENTION_DAYS} days after creation; account records last while the account does.`,
    policyUrl: 'https://firebase.google.com/support/privacy',
  },
  {
    name: 'OpenRouter, Inc.',
    role: 'Primary model routing',
    purpose: 'Routes each turn on the primary path to a named upstream inference provider',
    data: 'The messages and uploaded document text needed to answer the turn being processed',
    location: 'United States',
    retention: 'Does not store prompts or completions by default. Private input/output logging and the discounted "use my data" option are both off by default, leaving request metadata only (token counts, latency, cost). Does not train on inputs or outputs.',
    policyUrl: 'https://openrouter.ai/docs/guides/privacy/data-collection',
  },
  {
    name: 'Modal',
    role: 'Inference host for the primary model, reached through OpenRouter',
    purpose: 'Runs the primary model (z-ai/glm-5.3) that generates facilitator replies',
    data: 'The same prompt text, for as long as it takes to generate the reply',
    location: 'United States',
    retention: 'Zero prompt retention and no training on prompts, per the endpoint data policy OpenRouter publishes for Modal. We pin routing to Modal and put Z.ai’s own endpoint on the ignore list, so no session content reaches Z.ai the company.',
    policyUrl: 'https://modal.com/legal/privacy-policy',
  },
  {
    name: 'Anthropic',
    role: 'Fallback model, and the only path with web search',
    purpose: 'Answers a turn when the primary path fails or is too slow. Anthropic’s web search tool is attached only on this path; OpenRouter web search is switched off.',
    data: 'On a fallback turn, the messages and uploaded document text for that turn. Web search sends only a short query derived from the conversation, never the transcript or an uploaded document.',
    location: 'United States',
    retention: 'Deletes API inputs and outputs within 30 days by default and does not train on them. We do not hold a zero-data-retention agreement, so the 30-day default applies.',
    policyUrl: 'https://privacy.claude.com/en/articles/7996866-how-long-do-you-store-personal-data',
  },
  ...(PAYMENTS_ENABLED
    ? [{
        name: 'Stripe',
        role: 'Payments',
        purpose: 'Payment processing',
        data: 'Billing contact details. Card data is captured and held entirely by Stripe (PCI-DSS Level 1) and never reaches our servers.',
        location: 'United States',
        retention: 'Retains billing records independently for as long as financial regulation requires.',
        policyUrl: 'https://stripe.com/privacy',
      }]
    : []),
  {
    name: 'Resend',
    role: 'Transactional email',
    purpose: 'Sends post-session recap and summary email',
    data: 'Participant email addresses and session summary content',
    location: 'United States',
    retention: 'Retains sent message records per its own policy and the plan in force.',
    policyUrl: 'https://resend.com/legal/privacy-policy',
  },
  {
    name: 'Sentry',
    role: 'Error monitoring',
    purpose: 'Captures diagnostics when something breaks',
    data: 'Technical error data only: stack traces, browser and OS version, URL path. Text and inputs are masked and media is blocked.',
    location: 'United States',
    retention: 'Retains error events per its own policy and the plan in force.',
    policyUrl: 'https://sentry.io/privacy/',
  },
]

/** Categories of personal data processed, for Annex I(B) of the DPA and the privacy page. */
export const DATA_CATEGORIES: { label: string; detail: string }[] = [
  { label: 'Account identifiers', detail: 'Display name and account email address from Firebase Authentication (Google Sign-In or a verified email and password).' },
  { label: 'Session messages', detail: 'Everything participants type during a workshop, plus the replies the AI facilitators generate.' },
  { label: 'Uploaded documents', detail: 'Text extracted from files participants upload for review (PDF, Word, images), and the images themselves.' },
  { label: 'Reflection responses', detail: 'Individual answers given during reflection rounds.' },
  { label: 'Session artifacts', detail: 'Documents the workshop produces, such as system prompts and next-step plans.' },
  { label: 'Session feedback', detail: 'Ratings and free-text comments submitted at the end of a session.' },
  { label: 'Organization membership', detail: 'Which accounts belong to which workspace, and their role in it.' },
  { label: 'Usage counters', detail: 'Per-account and per-session quota counters used to bound AI spend.' },
]

/** Categories of data subject, for Annex I(B). */
export const DATA_SUBJECTS: string[] = [
  'Employees, contractors and other personnel of the customer who create or host a workshop session using an account with a verified email address',
  'Teammates the customer invites into a session, who join with a display name under an anonymous identity scoped to that room',
]

/** Technical and organizational measures, for Annex II of the DPA and the security page. */
export const SECURITY_MEASURES: string[] = [
  'Encryption in transit (TLS 1.2+, HTTPS only, HSTS preload) and at rest (AES-256) on Google Cloud',
  'Sessions are created and joined server-side only; the client cannot mint a session or a participant record',
  'Firestore security rules scope every read and write to the participants of that session (firestore.rules)',
  'Room passwords are salted, hashed with scrypt, and compared in constant time, with identical responses for a bad code and a bad password',
  'Every Cloud Function requires a Firebase ID token, and the acting user id comes from the verified token, never from the request body',
  'Agent system prompts live server-side; the chat endpoint accepts no client-supplied prompt',
  'Runtime secrets are held in Google Secret Manager and injected per function at runtime, never exposed to the browser',
  'An enforced Content-Security-Policy with no inline scripts and no eval, plus a documented allow-list of every outbound destination',
  'Error monitoring masks all text and inputs, blocks media, and records no session replay',
  'Per-account, per-session and global daily usage quotas bound AI spend and abuse',
  'Automated daily retention sweep deletes expired sessions and every subcollection under them',
  'Self-serve deletion in the app, with no ticket and no waiting on us',
]

/** What deleteMyData does (functions/src/account.ts), stated plainly. */
export const DELETION_BEHAVIOUR: string[] = [
  'Sessions you created are deleted outright, along with their messages, participants, artifacts, reflections and feedback.',
  'In a session you only joined, the transcript belongs to that team and stays, but your participant record and reflection answers are deleted and your messages are no longer attributed to you.',
  'Your profile document and your Firebase Authentication record are deleted.',
  'Lead-capture rows matching your verified email address are deleted.',
  'Organizations where you were the only member are deleted; otherwise your membership is removed.',
  'Payment records are retained for tax and accounting purposes with the account identifier stripped, so they no longer point at a person.',
]
